import { z } from 'zod';

// ── Types ──────────────────────────────────────────────────────────────────────

export type RoomType = {
  id: string;
  name: string;
  maxOccupancy: number | null;
  sortOrder: number;
  status: string;
};

export type LookupResult = {
  matched: boolean;
  ratePerNight?: number;
  totalAmount?: number;
  rateId?: string | null;
  segments?: Array<{ from: string; to: string; rate: number; nights: number; rateId?: string }>;
  reason?: string;
  uncoveredDates?: string[];
};

export type RoomRow = {
  id: string;
  hotelRoomTypeId: string;
  mealPlan: string;
  occupancyType: string;
  guestsPerRoom: number;
  roomsCount: number;
  ratePerNight: number | '';
  rateSource: 'contract' | 'adhoc' | 'unset';
  rateOverrideReason: string;
  contractedRateId: string | null;
  segments: LookupResult['segments'];
  isLookupLoading: boolean;
  maxOccupancy: number | null;
};

// ── Schemas ────────────────────────────────────────────────────────────────────

export const step1Schema = z
  .object({
    hotelId: z.string().min(1, 'Hotel is required'),
    hotelName: z.string(),
    hotelCity: z.string(),
    clientId: z.string().min(1, 'Client is required'),
    clientName: z.string(),
    clientType: z.string(),
    checkIn: z.string().min(1, 'Check-in date is required'),
    checkOut: z.string().min(1, 'Check-out date is required'),
  })
  .refine((d) => !d.checkIn || !d.checkOut || d.checkOut > d.checkIn, {
    message: 'Check-out must be after check-in',
    path: ['checkOut'],
  });

export const step3Schema = z
  .object({
    guestName: z.string().min(1, 'Guest name is required'),
    confNo: z.string().optional(),
    initialStatus: z.enum(['draft', 'tentative', 'confirmed']),
    tentativeUntil: z.string().optional(),
    remark: z.string().optional(),
  })
  .refine(
    (d) => d.initialStatus !== 'tentative' || !!d.tentativeUntil,
    { message: 'Tentative-until date is required', path: ['tentativeUntil'] },
  );

export type Step1Values = z.infer<typeof step1Schema>;
export type Step3Values = z.infer<typeof step3Schema>;

// ── Pure functions ─────────────────────────────────────────────────────────────

export function nightsCount(checkIn: string, checkOut: string): number {
  if (!checkIn || !checkOut) return 0;
  const d1 = new Date(checkIn);
  const d2 = new Date(checkOut);
  const diff = d2.getTime() - d1.getTime();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

/** Returns true if the row has a validation error that blocks step advancement. */
export function rowHasError(row: RoomRow, roomTypes: RoomType[]): boolean {
  if (!row.hotelRoomTypeId || !row.mealPlan || !row.occupancyType) return true;
  if (row.ratePerNight === '') return true;
  const maxOcc =
    row.maxOccupancy ?? roomTypes.find((rt) => rt.id === row.hotelRoomTypeId)?.maxOccupancy ?? null;
  if (maxOcc !== null && row.guestsPerRoom > maxOcc) return true;
  return false;
}

export function computeTotals(rows: RoomRow[], nights: number) {
  let totalRooms = 0;
  let totalRoomNights = 0;
  let grandTotal = 0;
  for (const row of rows) {
    totalRooms += row.roomsCount;
    totalRoomNights += row.roomsCount * nights;
    if (row.ratePerNight !== '') {
      grandTotal += (row.ratePerNight as number) * nights * row.roomsCount;
    }
  }
  return { totalRooms, totalRoomNights, grandTotal };
}

/**
 * Returns the patch to apply when the user edits the rate/night field.
 * If the row had a contract rate, flips it to adhoc.
 * If the value is cleared, resets to unset.
 */
export function applyRateChange(
  row: Pick<RoomRow, 'rateSource'>,
  val: number | '',
): Pick<RoomRow, 'ratePerNight' | 'rateSource'> {
  const newSource: RoomRow['rateSource'] =
    row.rateSource === 'contract' || row.rateSource === 'unset' ? 'adhoc' : row.rateSource;
  return {
    ratePerNight: val,
    rateSource: val === '' ? 'unset' : newSource,
  };
}
